<template>
  <div
    :class="[
      'therapy-items-bar__item',
      { 'therapy-items-bar__item--narrow': props.narrow },
      { 'therapy-items-bar__item--square': props.square },
    ]"
    @click="handleItemClick(item)"
  >
    <div
      v-if="item.type && !hideTag"
      class="therapy-items-bar__item-tag"
      :style="{ 'background-color': getItemTagColor(item) }"
    >
      {{ getTypeTitle(item) }}
    </div>
    <img
      v-show="state.isImageLoaded"
      class="therapy-items-bar__item-image"
      ref="imageRef"
      :src="item.img"
      alt=""
      @load="state.isImageLoaded = true"
      @error="state.isImageLoaded = false"
    />
    <div
      v-show="!state.isImageLoaded"
      class="therapy-items-bar__item-image therapy-items-bar__item-image--placeholder"
      :style="{ 'background-color': item.background }"
    ></div>
    <div
      :class="[
        'therapy-items-bar__item-title',
        {
          'therapy-items-bar__item-title--on-img': props.titleOnImg,
        },
      ]"
    >
      {{ getTitle(item) }}
    </div>
    <div v-if="item.text" class="therapy-items-bar__item-text">
      {{ getText(item) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import 'swiper/css';
import { nextTick, reactive, ref, watch } from 'vue';
import { TherapyBarItem } from '@/types/ui';
import { useInternalization } from '@/composables/useInternalization';
import { TherapyVideoType } from '@/types';

const state = reactive({
  isImageLoaded: false,
});
const imageRef = ref<HTMLImageElement | null>(null);

const props = defineProps<{
  item: TherapyBarItem;
  narrow?: boolean;
  square?: boolean;
  titleOnImg?: boolean;
  tabs?: { value: string; title: string }[];
  hideTag?: boolean;
}>();
const emit = defineEmits(['item:click']);

const { locale } = useInternalization();

watch(
  () => props.item,
  () => {
    nextTick(() => checkIfImagesLoaded());
  },
  { immediate: true }
);

const handleItemClick = (item: TherapyBarItem) => {
  emit('item:click', item);
};

const checkIfImagesLoaded = () => {
  if (!imageRef.value) {
    return;
  }

  state.isImageLoaded = !!imageRef.value?.complete;
};
const getTitle = (item: TherapyBarItem) => {
  if (!item.translations) {
    return item.title;
  }

  return item.translations[locale.value]?.title || item.title;
};
const getText = (item: TherapyBarItem) => {
  if (!item.translations) {
    return item.text;
  }

  return item.translations[locale.value]?.text || item.text;
};

const getTypeTitle = (item: TherapyBarItem) => {
  if (!item.translations) {
    return item.type;
  }

  return (
    props.tabs?.find((t) => t.value.toLowerCase() === item.type?.toLowerCase())
      ?.title || item.type?.toLowerCase()
  );
};

const getItemTagColor = (item: TherapyBarItem) => {
  if (item.type) {
    const colors: Record<TherapyVideoType, string> = {
      sound: '#5FC4FD',
      music: '#C467F0',
      asmr: '#FF87B9',
    };
    return colors[item.type] || colors.sound;
  }
};
</script>

<style lang="scss" scoped>
.therapy-items-bar {
  overflow: visible;
  margin-top: 1em;
  margin-bottom: 1em;

  &__header {
    font-weight: 600;
    font-size: 18px;
    line-height: 22px;
    color: #172659;
    margin-top: 1em;
  }

  &__item {
    cursor: pointer;
    position: relative;
    // width: 45vw;

    &-tag {
      position: absolute;
      top: 14px;
      left: 15px;
      background-color: #5fc4fd;
      border-radius: 12px;
      padding: 4px 8px;
      font-size: 0.8rem;
      color: #fff;
      z-index: 1;
    }

    &-image {
      width: 45vw;
      min-height: 200px;
      object-fit: contain;
      border-radius: 20px;
      position: relative;

      &--placeholder {
        background-color: #eef5fc;
        border-radius: 32px;
        height: 115px;
        position: relative;
      }
    }

    &-title {
      font-size: 1.2rem;
      font-weight: 500;
      line-height: 1.3;
      display: flex;
      align-items: flex-end;
      color: #172659;
      padding-left: 5px;

      &--on-img {
        position: absolute;
        left: 0;
        right: 0;
        bottom: 40px;
        color: #fff;
        z-index: 1;
        width: 100%;
        text-align: center;
        display: block;
        max-width: 170px;
        margin: 0 auto;
        padding-left: 0px;
      }
    }

    &-text {
      font-size: 0.9rem;
      margin-top: 0.2em;
      padding-left: 5px;
      font-weight: 500;
    }

    &--narrow .therapy-items-bar__item-image {
      max-height: 120px;
    }

    &--square .therapy-items-bar__item-image {
      width: 100% !important;
      max-height: unset !important;
    }
    &--square .therapy-items-bar__item-image--placeholder {
      width: 100% !important;
      max-height: unset !important;
    }
  }
}
</style>
